import React from 'react';
import { notification, List, Button } from 'antd';
import _ from 'lodash';
import { IconType } from 'antd/lib/notification';
import moment, { Moment } from 'moment';
import hash from 'object-hash';

interface Notification {
  key: string;
  type: IconType;
  message?: React.ReactNode;
  description?: React.ReactNode;
  shouldPersist?: boolean;
}

export default class NotificationUtility {
  // success notification
  public static success(
    message: React.ReactNode,
    description: React.ReactNode,
    duration?: number,
    shouldPersist?: boolean
  ) {
    NotificationUtility.showNotification('success', message, description, duration, shouldPersist);
  }

  // error notification
  public static error(
    message: React.ReactNode,
    description: React.ReactNode,
    duration?: number,
    shouldPersist?: boolean
  ) {
    NotificationUtility.showNotification('error', message, description, duration, shouldPersist);
  }

  // info notification
  public static info(
    message: React.ReactNode,
    description: React.ReactNode,
    duration?: number,
    shouldPersist?: boolean
  ) {
    NotificationUtility.showNotification('info', message, description, duration, shouldPersist);
  }

  // warning notification
  public static warning(
    message: React.ReactNode,
    description: React.ReactNode,
    duration?: number,
    shouldPersist?: boolean
  ) {
    NotificationUtility.showNotification('warning', message, description, duration, shouldPersist);
  }

  public static CloseNonPersistentNotifications() {
    const notificationList = NotificationUtility.notifications;
    notificationList
      .filter(x => !x.shouldPersist)
      .forEach(notice => {
        notification.close(notice.key);
        notificationList.splice(notificationList.indexOf(notice));
      });
  }

  private static notifications: Notification[] = [];

  private static CreateKey(
    type: string,
    message: React.ReactNode,
    description: React.ReactNode
  ): string {
    return hash((type as string) + hash(message) + hash(description));
  }

  private static showNotification(
    type: IconType,
    message: React.ReactNode,
    description: React.ReactNode,
    duration?: number,
    shouldPersist?: boolean
  ) {
    // If notification was already shown, remove and show again
    // Should only show two of the same type of message (should remove the oldest)
    const key = NotificationUtility.CreateKey(type, message, description);

    const notificationList = NotificationUtility.notifications;
    const sameNotificationsOfType = notificationList.filter(x => x.type === type);

    if (sameNotificationsOfType.length > 1) {
      // have to do housekeeping
      const sameNotification = sameNotificationsOfType.filter(x => x.key === key);

      if (sameNotification.length) {
        // Remove the old one and the new one will be added
        notification.close(key);
        notificationList.splice(notificationList.indexOf(sameNotification[0]), 1);
      } else {
        // If we have two persist message, then remove the oldest one.
        let oldestOrNonPersistent: Notification | undefined = sameNotificationsOfType[0];
        if (sameNotificationsOfType[0].shouldPersist) {
          oldestOrNonPersistent = sameNotificationsOfType[1].shouldPersist
            ? sameNotificationsOfType[0]
            : sameNotificationsOfType[1];
        }

        notification.close(oldestOrNonPersistent!.key);
        notificationList.splice(notificationList.indexOf(oldestOrNonPersistent!), 1);
      }
    }
    notificationList.push({ type, key, message, description, shouldPersist });
    notification[type]({ key, message, description, duration: shouldPersist ? 0 : duration, className: "notification-" + type });
  }
}
